import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ChevronLeft, Users, UserCog, CalendarDays, Layers, Plus } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/programs`;
const STATUS_LABEL = { active: 'Active', future: 'Future', archived: 'Archived' };
const STATUS_COLOR = {
  active: 'bg-emerald-600/20 text-emerald-300 border-emerald-600/40',
  future: 'bg-blue-600/20 text-blue-300 border-blue-600/40',
  archived: 'bg-slate-600/20 text-slate-300 border-slate-600/40',
};
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmtDate = (iso) => {
  if (!iso) return '';
  const [y, m, d] = String(iso).split('-').map(Number);
  if (!y || !m) return '';
  return `${MONTHS[m - 1]} ${d || 1}, ${y}`;
};
const dateRange = (p) => {
  const a = fmtDate(p.start_date), b = fmtDate(p.end_date);
  if (a && b) return `${a} – ${b}`;
  return a || b || '';
};

const Stat = ({ icon: Icon, label, value, isDark }) => (
  <div className={`flex items-center gap-1.5 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
    <Icon className="h-3.5 w-3.5 text-amber-500" /><span className="font-semibold">{value}</span>
    <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>{label}</span>
  </div>
);

const ProgramDetail = ({ orgId, program, onBack }) => {
  const [tab, setTab] = useState('teams');
  const [detail, setDetail] = useState(null);
  const [participants, setParticipants] = useState(null);
  const [filters, setFilters] = useState({ gender: '', team: '', role: '' });

  useEffect(() => {
    axios.get(`${API}/${orgId}/${program.id}`, { withCredentials: true }).then(r => setDetail(r.data)).catch(() => {});
  }, [orgId, program.id]);

  const loadParticipants = useCallback(() => {
    const p = {};
    if (filters.gender) p.gender = filters.gender;
    if (filters.team) p.team = filters.team;
    if (filters.role) p.role = filters.role;
    axios.get(`${API}/${orgId}/${program.id}/participants`, { params: p, withCredentials: true })
      .then(r => setParticipants(r.data)).catch(() => {});
  }, [orgId, program.id, filters]);

  useEffect(() => { if (tab === 'participants') loadParticipants(); }, [tab, loadParticipants]);

  const teams = detail?.teams || [];

  return (
    <div className="space-y-4" data-testid="program-detail">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-slate-400 hover:text-white" data-testid="program-back">
        <ChevronLeft className="h-4 w-4" /> All programs
      </button>
      <div>
        <h2 className="text-2xl font-bold text-white">{program.name}</h2>
        <div className="flex gap-4 text-sm mt-2">
          <Stat icon={Users} label="participants" value={detail?.program?.participant_count ?? program.participant_count} />
          <Stat icon={Users} label="players" value={detail?.program?.player_count ?? program.player_count} />
          <Stat icon={UserCog} label="staff" value={detail?.program?.staff_count ?? program.staff_count} />
          <Stat icon={Layers} label="teams" value={detail?.program?.team_count ?? program.team_count} />
        </div>
      </div>
      <div className="flex gap-1 border-b border-slate-700">
        {['teams', 'participants'].map(t => (
          <button key={t} onClick={() => setTab(t)} data-testid={`program-tab-${t}`}
            className={`px-3 py-2 text-sm capitalize ${tab === t ? 'text-amber-400 border-b-2 border-amber-500' : 'text-slate-400'}`}>{t}</button>
        ))}
      </div>

      {tab === 'teams' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="program-teams">
          {teams.length === 0 ? <p className="text-slate-400 text-sm">No teams in this program.</p>
            : teams.map(t => (
              <div key={t.group_id} className="border border-slate-700 rounded p-3 flex justify-between items-center bg-slate-800/50">
                <div><div className="text-white text-sm font-medium">{t.name}</div>
                  <div className="text-slate-500 text-xs font-mono">{t.roster_slug}</div></div>
                <Badge variant="outline" className="border-slate-600 text-slate-300">{t.player_count} players</Badge>
              </div>
            ))}
        </div>
      )}

      {tab === 'participants' && (
        <div className="space-y-3" data-testid="program-participants">
          <div className="flex flex-wrap gap-2">
            <select className="h-8 text-xs bg-slate-900 border border-slate-600 text-white rounded px-2" value={filters.gender}
              onChange={e => setFilters(f => ({ ...f, gender: e.target.value }))} data-testid="pf-gender">
              <option value="">All genders</option><option value="Male">Male</option><option value="Female">Female</option>
            </select>
            <select className="h-8 text-xs bg-slate-900 border border-slate-600 text-white rounded px-2" value={filters.team}
              onChange={e => setFilters(f => ({ ...f, team: e.target.value }))} data-testid="pf-team">
              <option value="">All teams</option>
              {teams.map(t => <option key={t.group_id} value={t.roster_slug}>{t.name}</option>)}
            </select>
            <select className="h-8 text-xs bg-slate-900 border border-slate-600 text-white rounded px-2" value={filters.role}
              onChange={e => setFilters(f => ({ ...f, role: e.target.value }))} data-testid="pf-role">
              <option value="">Players + Staff</option><option value="Player">Players</option><option value="Coach">Coaches</option>
            </select>
          </div>
          <div className="border border-slate-700 rounded max-h-[460px] overflow-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-900 text-slate-400 sticky top-0"><tr>
                <th className="text-left p-2">Name</th><th className="text-left p-2">Role</th>
                <th className="text-left p-2">DOB</th><th className="text-left p-2">Gender</th>
                <th className="text-left p-2">Team(s)</th><th className="text-left p-2">Family contact</th>
              </tr></thead>
              <tbody>
                {(participants?.players || []).map(p => (
                  <tr key={p.id} className="border-t border-slate-800 text-slate-200">
                    <td className="p-2">{p.name}</td><td className="p-2">{p.role}</td>
                    <td className="p-2">{p.dob || ''}</td><td className="p-2">{p.gender}</td>
                    <td className="p-2">{(p.teams || []).join(', ')}</td>
                    <td className="p-2 text-slate-400">{p.contact_name}{p.contact_email ? ` · ${p.contact_email}` : ''}</td>
                  </tr>
                ))}
                {(participants?.staff || []).map(s => (
                  <tr key={s.id} className="border-t border-slate-800 text-slate-300 bg-slate-800/30">
                    <td className="p-2">{s.name}</td><td className="p-2 text-amber-400">{s.role}</td>
                    <td className="p-2" colSpan={2}></td><td className="p-2"></td>
                    <td className="p-2 text-slate-400">{s.email}{s.phone ? ` · ${s.phone}` : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-slate-500">{participants?.player_count || 0} players · {participants?.staff_count || 0} staff</p>
        </div>
      )}
    </div>
  );
};

export const ProgramsPanel = ({ orgId, isDark = true, onCreateProgram }) => {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState('active');

  useEffect(() => {
    if (!orgId) return;
    setLoading(true);
    axios.get(`${API}/${orgId}`, { withCredentials: true })
      .then(r => setPrograms(r.data.programs || [])).catch(() => setPrograms([]))
      .finally(() => setLoading(false));
  }, [orgId]);

  if (selected) return <div className="mt-4"><ProgramDetail orgId={orgId} program={selected} onBack={() => setSelected(null)} /></div>;

  const title = isDark ? 'text-white' : 'text-slate-900';
  const sub = isDark ? 'text-slate-400' : 'text-slate-500';
  const cardCls = isDark
    ? 'bg-slate-800/50 border-slate-700 hover:border-amber-600/50'
    : 'bg-white border-slate-200 hover:border-amber-500/60';
  // Active tab groups Active + Future (PRS §5.2.1); Archived tab = archived.
  const sections = tab === 'active' ? ['active', 'future'] : ['archived'];

  return (
    <div className="mt-4 space-y-6" data-testid="programs-panel">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h2 className={`text-2xl font-bold ${title}`}>Programs</h2>
          <p className={`text-sm ${sub} mt-1`}>A program is a sport + season. It's derived from your teams and rosters, and you can also spin up a brand-new one with Create Program (or + New program… in the builder). Registration forms feed a program.</p>
        </div>
        {onCreateProgram && <Button size="sm" className="bg-amber-600 hover:bg-amber-700 shrink-0" onClick={onCreateProgram} data-testid="create-program"><Plus className="h-4 w-4 mr-1" /> Create Program</Button>}
      </div>
      <div className={`flex gap-1 border-b ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
        {[['active', 'Active'], ['archived', 'Archived']].map(([k, lbl]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`programs-tab-${k}`}
            className={`px-4 py-2 text-sm ${tab === k ? 'text-amber-500 border-b-2 border-amber-500' : sub}`}>{lbl}</button>
        ))}
      </div>
      {loading ? <p className={sub}>Loading…</p>
        : sections.every(st => !programs.some(p => p.status === st))
          ? <p className={sub} data-testid="programs-empty">No {tab} programs.</p>
          : sections.map(st => {
            const group = programs.filter(p => p.status === st);
            if (!group.length) return null;
            return (
              <div key={st}>
                <h3 className={`text-sm font-semibold mb-2 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{st === 'future' ? 'Future Programs' : STATUS_LABEL[st]}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {group.map(p => (
                    <Card key={p.id} className={`${cardCls} cursor-pointer transition-colors`}
                      onClick={() => setSelected(p)} data-testid={`program-card-${p.id}`}>
                      <CardContent className="p-4 space-y-3">
                        <div className="flex justify-between items-start gap-2">
                          <div><div className={`font-semibold ${title}`}>{p.name}</div>
                            <div className={`text-sm ${sub}`}>{dateRange(p) || p.season}</div></div>
                          <Badge variant="outline" className={STATUS_COLOR[p.status]}>{STATUS_LABEL[p.status]}</Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <Stat icon={Users} label="players" value={p.player_count} isDark={isDark} />
                          <Stat icon={UserCog} label="staff" value={p.staff_count} isDark={isDark} />
                          <Stat icon={Layers} label="teams" value={p.team_count} isDark={isDark} />
                          <Stat icon={CalendarDays} label="total" value={p.participant_count} isDark={isDark} />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            );
          })}
    </div>
  );
};

export default ProgramsPanel;
